import React from 'react';
import { PiFlask, PiSlidersHorizontal } from 'react-icons/pi';

import {
  SEED_TREATMENT_TWO_PANEL_BACK_TOOLTIP,
  SEED_TREATMENT_TWO_PANEL_DETAILS_LABEL,
  SEED_TREATMENT_TWO_PANEL_DETAILS_STRIP_TOOLTIP,
  SEED_TREATMENT_TWO_PANEL_PRODUCT_LABEL,
  SEED_TREATMENT_TWO_PANEL_PRODUCT_STRIP_TOOLTIP,
  STEP_SELECTOR_SAVE_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInputTask } from 'interfaces/IInputTask';

import SeedTreatmentFields from './SeedTreatmentFields';
import {
  getStepTwoPanelReopenTooltip,
  SeedDressDraft,
  StepSelectorOption,
} from './stepConfig';
import StepTwoPanel from './StepTwoPanel';

interface SeedTreatmentTwoPanelProps {
  products: StepSelectorOption[];
  highlightedProduct: StepSelectorOption | null;
  isOpen: boolean;
  task: IInputTask;
  gUnits: GUnit[];
  draft: SeedDressDraft;
  onDraftChange: (changes: Partial<SeedDressDraft>) => void;
  onProductClick: (product: StepSelectorOption) => void;
  onBackToProducts: () => void;
  onSave: () => void;
}

/**
 * Edit-mode two-panel for an existing Seed Treatment step: products on the left, the step's
 * rate mode / Seed treated / rate / unit / cost on the right (see StepTwoPanel for the layout).
 * Unlike Seed's variety pick, an explicit Save is needed, since each field is edited independently.
 */
const SeedTreatmentTwoPanel: React.FC<SeedTreatmentTwoPanelProps> = ({
  products,
  highlightedProduct,
  isOpen,
  task,
  gUnits,
  draft,
  onDraftChange,
  onProductClick,
  onBackToProducts,
  onSave,
}) => {
  return (
    <StepTwoPanel
      primaryOptions={products}
      highlightedPrimary={highlightedProduct}
      isSecondaryOpen={isOpen}
      onPrimaryClick={onProductClick}
      onBackToPrimaryList={onBackToProducts}
      primaryStripLabel={SEED_TREATMENT_TWO_PANEL_PRODUCT_LABEL}
      primaryStripIcon={
        <PiFlask size={14} className="flex-shrink-0 text-gray-dark" />
      }
      primaryStripTooltip={SEED_TREATMENT_TWO_PANEL_PRODUCT_STRIP_TOOLTIP}
      backTooltip={SEED_TREATMENT_TWO_PANEL_BACK_TOOLTIP}
      secondaryContent={
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <SeedTreatmentFields
            task={task}
            gUnits={gUnits}
            rateMode={draft.rateMode}
            rate={draft.rate}
            unit={draft.unit}
            cost={draft.cost}
            linkedStepId={draft.linkedStepId}
            className="min-h-0 flex-1 p-3"
            onRateModeChange={(rateMode) => onDraftChange({ rateMode })}
            onRateChange={(rate) => onDraftChange({ rate })}
            onUnitChange={(unit) => onDraftChange({ unit })}
            onCostChange={(cost) => onDraftChange({ cost })}
            onTargetSelect={(linkedStepId) => onDraftChange({ linkedStepId })}
          />
          <div className="flex-shrink-0 p-3">
            <button
              type="button"
              className="w-full rounded-[var(--radius-button)] bg-success-dark px-3 py-1.5 text-sm font-medium text-white"
              onClick={onSave}
            >
              {STEP_SELECTOR_SAVE_LABEL}
            </button>
          </div>
        </div>
      }
      secondaryStripActiveLabel={SEED_TREATMENT_TWO_PANEL_DETAILS_LABEL}
      secondaryStripActiveIcon={
        <PiSlidersHorizontal
          size={14}
          className="flex-shrink-0 text-gray-dark"
        />
      }
      secondaryStripActiveTooltip={
        highlightedProduct
          ? getStepTwoPanelReopenTooltip(highlightedProduct.label, 'details')
          : ''
      }
      onReopenSecondary={onProductClick}
      secondaryStripInertLabel={SEED_TREATMENT_TWO_PANEL_DETAILS_LABEL}
      secondaryStripInertIcon={
        <PiSlidersHorizontal
          size={14}
          className="flex-shrink-0 text-gray-medium"
        />
      }
      secondaryStripInertTooltip={
        SEED_TREATMENT_TWO_PANEL_DETAILS_STRIP_TOOLTIP
      }
    />
  );
};

export default SeedTreatmentTwoPanel;
